import { Box, Divider, Grid, GridItem, Image, Text, VStack } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useDebouncedCallback } from 'hooks/useDebounce';
import { IMAGE_EDITED, Mixpanel } from 'libs/third-party/Mixpanel';
import { useAppSelector } from 'modules/Editor/store';
import { selectActiveWidgets } from 'modules/Editor/store/widgetSelector';
import { SliderNumberInput } from 'modules/common/components/Input';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from 'theme/colors';
import { getColorWithAlphaString } from 'utils/color';
import { useWidget } from 'widgets/sdk';
import { getAllMultiEditWidgetIds } from 'widgets/sdk/Toolbar/MultiWidgetToolbar/MultiWidgetToolbar.helpers';

import { ADJUSTMENT_MAX, ADJUSTMENT_MIN, FILTER_LIST, TRACKING_DEBOUNCE_MS } from '../../ImageWidget.config';
import { calculateFilterAdjustment } from '../../ImageWidget.helpers';
import { FilterEffectListProps, FilterName, ImageFilterEffectProps, ImageWidgetData } from '../../ImageWidget.types';

const FILTER_SLIDER_WIDTH = 132;

const GridItemImageFilterSample = styled(GridItem)`
  &:hover {
    background: ${getColorWithAlphaString(colors.hover.gray, 0.08)};

    span.chakra-text {
      color: ${colors.font[500]};
    }
    color: ${colors.font[500]};
  }
  &.active {
    background: ${getColorWithAlphaString(colors.hover.blue, 0.08)};
    span.chakra-text {
      color: ${colors.upgrade.blue[700]};
    }
  }
`;

interface SelectedWidgetsProps {
  shouldBatchUpdate: boolean;
}

export const FilterEffectItem = memo(
  ({ filterName, sampleSrc, onFilterEffect, selectedFilter }: Readonly<ImageFilterEffectProps>) => {
    const handleClick = () => {
      onFilterEffect(filterName);
    };

    return (
      <GridItemImageFilterSample
        w='full'
        p={2}
        pb={1}
        textAlign='center'
        borderRadius={'base'}
        onClick={handleClick}
        className={selectedFilter === filterName || (!selectedFilter && filterName === 'None') ? 'active' : ''}
        as={'button'}
      >
        <Image boxSize='88px' objectFit='cover' src={sampleSrc} borderRadius={1} />
        <Text fontSize={'sm'} textTransform='capitalize' color={colors.black} fontWeight={'medium'} as={'span'}>
          {filterName}
        </Text>
      </GridItemImageFilterSample>
    );
  },
);

FilterEffectItem.displayName = 'FilterEffectItem';

export const FilterEffectList = memo(
  ({ filterList, selectedFilter, onFilterEffect }: Readonly<FilterEffectListProps>) => {
    return (
      <Grid templateColumns='repeat(3, 1fr)' gap={2}>
        {filterList.map((item) => (
          <FilterEffectItem
            filterName={item.filterName}
            sampleSrc={item.sampleSrc}
            selectedFilter={selectedFilter}
            onFilterEffect={onFilterEffect}
            key={`filter_effect_item_${item.filterName}`}
          />
        ))}
      </Grid>
    );
  },
);

export const FilterEffects = ({ shouldBatchUpdate }: SelectedWidgetsProps) => {
  const { filter: { appliedFilterName = 'None' } = {}, updateWidget } = useWidget<ImageWidgetData>();

  const handleFilterEffect = useCallback(
    (filterName: FilterName) => {
      const filterIntensity = 50;
      updateWidget({
        filter: {
          appliedFilterName: filterName,
          filterIntensity,
          effect: {
            brightness: calculateFilterAdjustment('brightness', filterIntensity, filterName),
            contrast: calculateFilterAdjustment('contrast', filterIntensity, filterName),
            saturation: calculateFilterAdjustment('saturation', filterIntensity, filterName),
            tint: calculateFilterAdjustment('tint', filterIntensity, filterName),
          },
        },
      });

      Mixpanel.track(IMAGE_EDITED, {
        image_effect: IMAGE_EDITED.image_effect.Filter,
        batch_updated: shouldBatchUpdate,
      });
    },
    [shouldBatchUpdate, updateWidget],
  );

  return (
    <Box py={2} textAlign='left'>
      <FilterEffectList
        filterList={FILTER_LIST}
        selectedFilter={appliedFilterName}
        onFilterEffect={handleFilterEffect}
      />
    </Box>
  );
};

FilterEffectList.displayName = 'FilterEffectList';

interface AdjusterProps {
  onTrack: (imageEffect: string) => void;
}

export const BrightnessAdjuster = ({ onTrack }: Readonly<AdjusterProps>) => {
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'imageToolbar', useSuspense: false });
  const { filter: { effect: { brightness = 0 } = { brightness: 0 } } = {}, updateWidget } =
    useWidget<ImageWidgetData>();

  const handleChangeSlider = (newVal: number) => {
    updateWidget({
      filter: {
        appliedFilterName: 'None',
        filterIntensity: 50,
        effect: {
          brightness: newVal,
        },
      },
    });

    onTrack(IMAGE_EDITED.image_effect.Adjust);
  };

  return (
    <Box w={'full'} px={6}>
      <SliderNumberInput
        title={t('settingsSideBar.brightness')}
        value={brightness}
        onChange={handleChangeSlider}
        minValue={ADJUSTMENT_MIN}
        maxValue={ADJUSTMENT_MAX}
        step={1}
        precision={1}
        suffix={''}
        sliderWidth={FILTER_SLIDER_WIDTH}
        fontWeight='normal'
        containerProps={{ pt: 3, gap: '0.375rem' }}
      />
    </Box>
  );
};

export const ContrastAdjuster = ({ onTrack }: Readonly<AdjusterProps>) => {
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'imageToolbar', useSuspense: false });
  const { filter: { effect: { contrast = 0 } = { contrast: 0 } } = {}, updateWidget } = useWidget<ImageWidgetData>();

  const handleChangeSlider = (newVal: number) => {
    updateWidget({
      filter: {
        appliedFilterName: 'None',
        filterIntensity: 50,
        effect: {
          contrast: newVal,
        },
      },
    });

    onTrack(IMAGE_EDITED.image_effect.Adjust);
  };

  return (
    <Box w={'full'} px={6}>
      <SliderNumberInput
        title={t('settingsSideBar.contrast')}
        value={contrast}
        onChange={handleChangeSlider}
        minValue={ADJUSTMENT_MIN}
        maxValue={ADJUSTMENT_MAX}
        step={1}
        precision={1}
        suffix={''}
        sliderWidth={FILTER_SLIDER_WIDTH}
        fontWeight='normal'
        containerProps={{ pt: 3, gap: '0.375rem' }}
      />
    </Box>
  );
};

export const SaturationAdjuster = ({ onTrack }: Readonly<AdjusterProps>) => {
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'imageToolbar', useSuspense: false });
  const { filter: { effect: { saturation = 0 } = { saturation: 0 } } = {}, updateWidget } =
    useWidget<ImageWidgetData>();

  const handleChangeSlider = (newVal: number) => {
    updateWidget({
      filter: {
        appliedFilterName: 'None',
        filterIntensity: 50,
        effect: {
          saturation: newVal,
        },
      },
    });

    onTrack(IMAGE_EDITED.image_effect.Adjust);
  };

  return (
    <Box w={'full'} px={6}>
      <SliderNumberInput
        title={t('settingsSideBar.saturation')}
        value={saturation}
        onChange={handleChangeSlider}
        minValue={ADJUSTMENT_MIN}
        maxValue={ADJUSTMENT_MAX}
        step={1}
        precision={1}
        suffix={''}
        sliderWidth={FILTER_SLIDER_WIDTH}
        fontWeight='normal'
        containerProps={{ pt: 3, gap: '0.375rem' }}
      />
    </Box>
  );
};

export const TintAdjuster = ({ onTrack }: Readonly<AdjusterProps>) => {
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'imageToolbar', useSuspense: false });
  const { filter: { effect: { tint = 0 } = { tint: 0 } } = {}, updateWidget } = useWidget<ImageWidgetData>();

  const handleChangeSlider = (newVal: number) => {
    updateWidget({
      filter: {
        appliedFilterName: 'None',
        filterIntensity: 50,
        effect: {
          tint: newVal,
        },
      },
    });

    onTrack(IMAGE_EDITED.image_effect.Adjust);
  };

  return (
    <Box w={'full'} px={6}>
      <SliderNumberInput
        title={t('settingsSideBar.tint')}
        value={tint}
        onChange={handleChangeSlider}
        minValue={ADJUSTMENT_MIN}
        maxValue={ADJUSTMENT_MAX}
        step={1}
        precision={1}
        suffix={''}
        sliderWidth={FILTER_SLIDER_WIDTH}
        fontWeight='normal'
        containerProps={{ pt: 3, gap: '0.375rem' }}
        sliderTrackBackground={
          'linear-gradient(90deg, #FF0000, #FAFF00, #00FB0A, #00FFFF, #0500FF, #FF00F5, #FF000000)'
        }
        sliderBackground={'transparent'}
      />
    </Box>
  );
};

export const FilterIntensityAdjuster = ({ onTrack }: Readonly<AdjusterProps>) => {
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'imageToolbar', useSuspense: false });
  const { filter: { appliedFilterName = 'None', filterIntensity = 0 } = {}, updateWidget } =
    useWidget<ImageWidgetData>();

  const handleChangeSlider = (newVal: number) => {
    updateWidget({
      filter: {
        filterIntensity: newVal,
        effect: {
          brightness: calculateFilterAdjustment('brightness', newVal, appliedFilterName),
          contrast: calculateFilterAdjustment('contrast', newVal, appliedFilterName),
          saturation: calculateFilterAdjustment('saturation', newVal, appliedFilterName),
          tint: calculateFilterAdjustment('tint', newVal, appliedFilterName),
        },
      },
    });

    onTrack(IMAGE_EDITED.image_effect.Filter);
  };

  return (
    <Box mt={2} px={6} w={'full'}>
      <SliderNumberInput
        title={t('settingsSideBar.intensity')}
        value={filterIntensity}
        onChange={handleChangeSlider}
        minValue={0}
        maxValue={100}
        step={1}
        precision={1}
        suffix={'%'}
        sliderWidth={FILTER_SLIDER_WIDTH}
        fontWeight='normal'
        containerProps={{ pt: 3, gap: '0.375rem' }}
        disabled={appliedFilterName === 'None'}
      />
    </Box>
  );
};

export const Filters = memo(() => {
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'imageToolbar', useSuspense: false });
  const activeWidgets = useAppSelector(selectActiveWidgets);
  const selectedWidgets = getAllMultiEditWidgetIds(activeWidgets);
  const shouldBatchUpdate = selectedWidgets?.length > 1;

  const handleTrackAdjust = useDebouncedCallback((imageEffect: string) => {
    Mixpanel.track(IMAGE_EDITED, { image_effect: imageEffect, batch_updated: shouldBatchUpdate });
  }, TRACKING_DEBOUNCE_MS);

  return (
    <VStack>
      <FilterEffects shouldBatchUpdate={shouldBatchUpdate} />
      <FilterIntensityAdjuster onTrack={handleTrackAdjust} />
      <Divider my={4} />
      <Box w={'full'} textAlign={'left'} px={6}>
        <Text fontSize={'sm'} fontWeight='bold' my={2} w={'full'} textAlign={'left'}>
          {t('settingsSideBar.adjust')}
        </Text>
      </Box>
      <BrightnessAdjuster onTrack={handleTrackAdjust} />
      <ContrastAdjuster onTrack={handleTrackAdjust} />
      <SaturationAdjuster onTrack={handleTrackAdjust} />
      <TintAdjuster onTrack={handleTrackAdjust} />
    </VStack>
  );
});

Filters.displayName = 'Filters';

export default Filters;
